import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Button, Modal, Select, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { submissionTranslations } from '../../../components/SubmissionV2/translations'
import { MasterVehicleBrand, MasterVehicleModel } from '../../../services/master/vehicle.interfaces'
import { getVehicleBrands, getVehicleModels } from '../../../services/master/vehicles.services'
import { getStandardVehiclePricePublic } from '../../../services/submission'
import MixpanelTracker from '../../../services/track/mixpanel'
import { useTrackModalOpen } from '../../../utils/useTrackModalOpen'
import { PageLang } from '../pageLang'

interface TierEstimate {
  utilisationPct: number
  days: number
  income: number
}

interface EarningsResult {
  dailyRate: number
  monthlyRate: number
  starter: TierEstimate
  pro: TierEstimate
}

const STARTER_UTILISATION_PCT = 50
const PRO_UTILISATION_PCT = 80
const DAYS_PER_MONTH = 30

function getYearOptions(): { label: string; value: string }[] {
  const thisYear = new Date().getFullYear()
  const firstYear = thisYear - 10
  const years: { label: string; value: string }[] = []
  for (let y = thisYear; y >= firstYear; y--) {
    years.push({ label: String(y), value: String(y) })
  }
  return years
}

function buildTierEstimate(dailyRate: number, utilisationPct: number): TierEstimate {
  const days = Math.round((utilisationPct / 100) * DAYS_PER_MONTH)
  return {
    utilisationPct,
    days,
    income: Math.round(dailyRate * days),
  }
}

const carEstimateSelectStyles = `
  .car-estimate-select .ant-select-selector {
    border-radius: 8px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }
  .car-estimate-modal-fullheight .ant-modal {
    top: 0;
    padding-bottom: 0;
    height: 100vh;
  }
  .car-estimate-modal-fullheight .ant-modal-content {
    height: 100vh;
    display: flex;
    flex-direction: column;
    border-radius: 0;
  }
  .car-estimate-modal-fullheight .ant-modal-body {
    flex: 1;
    overflow-y: auto;
  }
`

export interface CarEstimateModalProps {
  lang: PageLang
  open: boolean
  onClose: () => void
}

export const CarEstimateModal = ({ lang, open, onClose }: CarEstimateModalProps) => {
  const t = submissionTranslations[lang].listing.proIntro.earningsCalculator
  useTrackModalOpen('Login Page Car Estimate Modal Opened', open)

  const [brandList, setBrandList] = useState<MasterVehicleBrand[]>([])
  const [modelList, setModelList] = useState<MasterVehicleModel[]>([])
  const [brand, setBrand] = useState<string>()
  const [selectedModel, setSelectedModel] = useState<MasterVehicleModel>()
  const [year, setYear] = useState<string>()
  const [brandLoading, setBrandLoading] = useState(false)
  const [modelLoading, setModelLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<EarningsResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = getYearOptions()

  useEffect(() => {
    if (!open) return
    setBrandLoading(true)
    getVehicleBrands()
      .then(setBrandList)
      .catch(() => setBrandList([]))
      .finally(() => setBrandLoading(false))
  }, [open])

  useEffect(() => {
    if (!brand) {
      setModelList([])
      setModelLoading(false)
      return
    }
    let cancelled = false
    setModelList([])
    setModelLoading(true)
    getVehicleModels(brand)
      .then((models) => {
        if (cancelled) return
        setModelList(models)
      })
      .catch(() => {
        if (cancelled) return
        setModelList([])
      })
      .finally(() => {
        if (cancelled) return
        setModelLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [brand])

  useEffect(() => {
    if (!open) {
      setBrand(undefined)
      setSelectedModel(undefined)
      setYear(undefined)
      setResult(null)
      setError(null)
    }
  }, [open])

  const handleClose = () => {
    MixpanelTracker.track('Login Page Car Estimate Modal Close Click')
    onClose()
  }

  const handleCalculate = async () => {
    if (!brand || !selectedModel || !year) return
    MixpanelTracker.track('Login Page Car Estimate Calculate Click', {
      brand,
      model: selectedModel.key,
      year,
    })
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const price = await getStandardVehiclePricePublic(
        brand,
        selectedModel.key,
        selectedModel.sub_model,
        year,
      )

      const dailyRate = price.dailyRateNormal || 0
      const monthlyRate = price.monthlyRate || 0

      if (dailyRate <= 0) {
        throw new Error('Pricing not available')
      }

      setResult({
        dailyRate,
        monthlyRate,
        starter: buildTierEstimate(dailyRate, STARTER_UTILISATION_PCT),
        pro: buildTierEstimate(dailyRate, PRO_UTILISATION_PCT),
      })
    } catch (err: any) {
      setError(err?.response?.data?.message || t.notFoundError)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      maskClosable={false}
      footer={null}
      width={490}
      zIndex={1050}
      wrapClassName="car-estimate-modal-fullheight"
      bodyStyle={{ padding: '28px', fontFamily: 'Poppins, sans-serif' }}
      style={{ top: 0, paddingBottom: 0 }}
    >
      <style>{carEstimateSelectStyles}</style>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#212427', marginBottom: '8px' }}>
        {t.modalTitle}
      </h2>
      <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginBottom: '20px' }}>
        {t.modalSubtitle}
      </p>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: '#212427', marginBottom: '6px' }}>
          {t.brandLabel}
        </div>
        <Select
          style={{ width: '100%' }}
          className="car-estimate-select"
          placeholder={t.brandPlaceholder}
          value={brand}
          onChange={(value) => {
            setBrand(value)
            setSelectedModel(undefined)
            setResult(null)
            setError(null)
          }}
          showSearch
          optionFilterProp="label"
          loading={brandLoading}
          notFoundContent={brandLoading ? <Spin size="small" /> : undefined}
          options={brandList.map((b) => ({
            label: lang === 'EN' ? b.description_en : b.description_th,
            value: b.key,
          }))}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: '#212427', marginBottom: '6px' }}>
          {t.modelLabel}
        </div>
        <Select
          style={{ width: '100%' }}
          className="car-estimate-select"
          placeholder={t.modelPlaceholder}
          value={selectedModel ? String(modelList.indexOf(selectedModel)) : undefined}
          onChange={(value) => {
            setSelectedModel(modelList[Number(value)])
            setResult(null)
            setError(null)
          }}
          disabled={!brand}
          showSearch
          optionFilterProp="label"
          loading={modelLoading}
          notFoundContent={modelLoading ? <Spin size="small" /> : undefined}
          options={modelList.map((m, index) => ({
            label: lang === 'EN' ? m.description_en : m.description_th,
            value: String(index),
          }))}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: '#212427', marginBottom: '6px' }}>{t.yearLabel}</div>
        <Select
          style={{ width: '100%' }}
          className="car-estimate-select"
          placeholder={t.yearPlaceholder}
          value={year}
          onChange={(value) => {
            setYear(value)
            setResult(null)
            setError(null)
          }}
          options={yearOptions}
        />
      </div>

      <Button
        type="primary"
        block
        size="large"
        shape="round"
        loading={loading}
        disabled={!brand || !selectedModel || !year}
        onClick={handleCalculate}
        style={{ backgroundColor: '#0048B3', borderColor: '#0048B3', height: '48px' }}
      >
        {t.calculateBtn}
      </Button>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            backgroundColor: '#FFF1F0',
            border: '1px solid #FFCCC7',
            borderRadius: '10px',
            padding: '12px',
            marginTop: '16px',
          }}
        >
          <ExclamationCircleOutlined
            style={{ color: '#CF1322', fontSize: '14px', marginTop: '1px' }}
          />
          <div style={{ fontSize: '13px', color: '#CF1322', lineHeight: '1.5' }}>{error}</div>
        </div>
      )}

      {result && (
        <>
          <div style={{ borderTop: '1px solid #EEF0F3', margin: '24px 0 16px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#212427', marginBottom: '4px' }}>
            {t.resultsTitle}
          </h3>
          <p
            style={{
              fontSize: '12.5px',
              color: '#8C97A7',
              lineHeight: '1.5',
              marginBottom: '16px',
            }}
          >
            {t.resultsSubtitle}
          </p>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <div
              style={{ flex: 1, backgroundColor: '#F4F5F9', borderRadius: '10px', padding: '14px' }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#212427' }}>
                {t.perDayRateLabel}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#212427', margin: '2px 0' }}>
                THB {result.dailyRate.toLocaleString()}
              </div>
              <div style={{ fontSize: '11px', color: '#8C97A7' }}>{t.perDayRateNote}</div>
            </div>

            <div
              style={{ flex: 1, backgroundColor: '#F4F5F9', borderRadius: '10px', padding: '14px' }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#212427' }}>
                {t.perMonthRateLabel}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#212427', margin: '2px 0' }}>
                THB {result.monthlyRate.toLocaleString()}
              </div>
              <div style={{ fontSize: '11px', color: '#8C97A7' }}>{t.perMonthRateNote}</div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              backgroundColor: '#F4F5F9',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '16px',
            }}
          >
            <InfoCircleOutlined style={{ color: '#8C97A7', fontSize: '14px', marginTop: '1px' }} />
            <div style={{ fontSize: '11.5px', color: '#667085', lineHeight: '1.5' }}>
              {t.standardPricingNote}
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#F4F5F9',
              borderRadius: '10px',
              padding: '14px',
              marginBottom: '12px',
            }}
          >
            <div
              style={{ fontSize: '14px', fontWeight: 600, color: '#212427', marginBottom: '10px' }}
            >
              {t.starterLabel}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#212427',
                marginBottom: '6px',
              }}
            >
              <span>{t.utilisationRateLabel}</span>
              <span>{result.starter.utilisationPct}%</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#212427',
                marginBottom: '6px',
              }}
            >
              <span>{t.estRentalDaysLabel}</span>
              <span>{result.starter.days}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#212427',
              }}
            >
              <span>{t.estIncomeLabel}</span>
              <span style={{ fontSize: '15px', fontWeight: 700 }}>
                THB {result.starter.income.toLocaleString()}
              </span>
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#EAF1FF',
              border: '1px solid #C7DBFF',
              borderRadius: '10px',
              padding: '14px',
              marginBottom: '12px',
            }}
          >
            <div
              style={{ fontSize: '14px', fontWeight: 600, color: '#0048B3', marginBottom: '10px' }}
            >
              {t.proLabel}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#0048B3',
                marginBottom: '6px',
              }}
            >
              <span>{t.utilisationRateLabel}</span>
              <span>{result.pro.utilisationPct}%</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#0048B3',
                marginBottom: '6px',
              }}
            >
              <span>{t.estRentalDaysLabel}</span>
              <span>{result.pro.days}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#0048B3',
              }}
            >
              <span>{t.estIncomeLabel}</span>
              <span style={{ fontSize: '15px', fontWeight: 700 }}>
                THB {result.pro.income.toLocaleString()}
              </span>
            </div>
          </div>

          <p style={{ fontSize: '11px', color: '#8C97A7', lineHeight: '1.5' }}>{t.disclaimer}</p>
        </>
      )}
    </Modal>
  )
}

export default CarEstimateModal
